import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, MessageSquare, Ticket, Plus, Trash2, Loader2, Pencil, Zap, CheckCircle2, XCircle, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  ai: { label: 'Inteligência Artificial', icon: Brain, color: 'text-purple-500', bg: 'bg-purple-50 border-purple-200' },
  notification: { label: 'Notificações', icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200' },
  itsm: { label: 'ITSM / Tickets', icon: Ticket, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200' },
};

const FIELD_LABELS = {
  api_key: 'API Key', model: 'Modelo', webhook_url: 'Webhook URL', channel: 'Canal',
  url: 'URL', method: 'Método HTTP', headers: 'Headers (JSON)',
  instance_url: 'URL da Instância', username: 'Usuário', password: 'Senha',
  assignment_group: 'Grupo de Atribuição', email: 'Email', api_token: 'API Token', project_key: 'Project Key',
};

const MODEL_OPTIONS = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-3.5-turbo'],
  claude: ['claude-sonnet-4-5', 'claude-haiku-4-5'],
  gemini: ['gemini-2.0-flash', 'gemini-1.5-pro'],
};

export function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [availableTypes, setAvailableTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ category: '', provider: '', name: '', config: {} });
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations`, { headers: getAuthHeader() });
      setIntegrations(res.data.integrations || []);
      setAvailableTypes(res.data.available_types || {});
    } catch (err) { toast.error('Erro ao carregar integrações'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = (category, provider) => {
    const meta = availableTypes[category]?.[provider] || {};
    const config = {};
    (meta.fields || []).forEach(f => { config[f] = ''; });
    setForm({ category, provider, name: meta.name || '', config });
    setEditItem(null);
    setShowCreate(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ category: item.category, provider: item.provider, name: item.name, config: { ...item.config } });
    setShowCreate(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/integrations/${editItem.id}`, { name: form.name, config: form.config }, { headers: getAuthHeader() });
        toast.success('Integração atualizada');
      } else {
        await axios.post(`${API}/integrations`, form, { headers: getAuthHeader() });
        toast.success('Integração criada');
      }
      setShowCreate(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta integração?')) return;
    try {
      await axios.delete(`${API}/integrations/${id}`, { headers: getAuthHeader() });
      toast.success('Integração excluída');
      fetchData();
    } catch (err) { toast.error('Erro ao excluir'); }
  };

  const handleToggle = async (item) => {
    try {
      await axios.put(`${API}/integrations/${item.id}`, { is_active: !item.is_active }, { headers: getAuthHeader() });
      fetchData();
    } catch (err) { toast.error('Erro'); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(`${API}/integrations/${id}/test`, {}, { headers: getAuthHeader() });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) { toast.error('Erro no teste'); }
    finally { setTesting(null); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-[300px] w-full" /></div>;

  const grouped = { ai: [], notification: [], itsm: [] };
  integrations.forEach(i => { if (grouped[i.category]) grouped[i.category].push(i); });

  const currentFields = editItem
    ? (availableTypes[editItem.category]?.[editItem.provider]?.fields || [])
    : (availableTypes[form.category]?.[form.provider]?.fields || []);

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h2 className="text-lg font-semibold">Integrações</h2>
        <p className="text-sm text-muted-foreground">Conecte IA, notificações e ITSM à sua plataforma</p>
      </div>

      <Tabs defaultValue="ai">
        <TabsList>
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <TabsTrigger key={key} value={key} className="flex items-center gap-1.5">
                <Icon className="h-4 w-4" />{meta.label}
                {grouped[key].length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{grouped[key].length}</Badge>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(CATEGORY_META).map(([catKey, catMeta]) => (
          <TabsContent key={catKey} value={catKey} className="mt-4 space-y-4">
            {/* Existing integrations */}
            {grouped[catKey].map(item => (
              <Card key={item.id} className="border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${catMeta.bg}`}>
                      <catMeta.icon className={`h-5 w-5 ${catMeta.color}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{item.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item.provider}</p>
                    </div>
                    <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs ml-2">
                      {item.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleTest(item.id)} disabled={testing === item.id}>
                      {testing === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                      <span className="ml-1">Testar</span>
                    </Button>
                    <Switch checked={item.is_active} onCheckedChange={() => handleToggle(item)} />
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Add new */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(availableTypes[catKey] || {}).map(([provKey, provMeta]) => {
                const exists = grouped[catKey].some(i => i.provider === provKey);
                return (
                  <Card key={provKey} className={`border cursor-pointer hover:shadow-md transition-shadow ${exists ? 'opacity-50' : ''}`}
                    onClick={() => !exists && openCreate(catKey, provKey)}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${catMeta.bg}`}>
                        <catMeta.icon className={`h-5 w-5 ${catMeta.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{provMeta.name}</p>
                        <p className="text-xs text-muted-foreground">{exists ? 'Já configurado' : 'Clique para configurar'}</p>
                      </div>
                      {exists ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Plus className="h-4 w-4 text-muted-foreground" />}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditItem(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Editar Integração' : 'Nova Integração'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            {currentFields.map(field => (
              <div key={field} className="space-y-2">
                <Label>{FIELD_LABELS[field] || field}</Label>
                {field === 'model' && MODEL_OPTIONS[form.provider] ? (
                  <Select value={form.config[field] || ''} onValueChange={(v) => setForm({ ...form, config: { ...form.config, [field]: v } })}>
                    <SelectTrigger><SelectValue placeholder="Selecionar modelo" /></SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS[form.provider].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : field === 'method' ? (
                  <Select value={form.config[field] || 'POST'} onValueChange={(v) => setForm({ ...form, config: { ...form.config, [field]: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={field.includes('password') || field.includes('key') || field.includes('token') ? 'password' : 'text'}
                    value={form.config[field] || ''}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, [field]: e.target.value } })}
                    placeholder={FIELD_LABELS[field] || field}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditItem(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editItem ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
